import { router } from '@inertiajs/react'

/**
 * Shared partial reload after voiding a task log: Show header badge (`instrument`) +
 * Tasks schedule (`currentSchedule`, including `latestQcPassed` for the failed ribbon).
 * Used by Calendar/Failures voids and Tasks "View Last Performed" void so both paths stay
 * in sync without a full page reload (which would remount Reports dialogs).
 */
export const reloadInstrumentAndScheduleAfterVoid = (): void => {
  router.reload({ only: ['instrument', 'currentSchedule'] })
}

/** Tasks "View Last Performed" void — alias of {@link reloadInstrumentAndScheduleAfterVoid}. */
export const reloadInstrumentAndScheduleAfterTasksVoid = reloadInstrumentAndScheduleAfterVoid

/**
 * Calendar Log Details / Failures / day-occurrences void: same schedule+instrument reload as
 * Tasks void. Alias kept for call-site readability (Reports void must refresh Tasks props too).
 */
export const reloadInstrumentShowPropsAfterCalendarDelete = reloadInstrumentAndScheduleAfterVoid

/** Calendar cells (local fetch) + Show header + Tasks schedule (Inertia partial reload). */
export const runCalendarLogDeleteRefresh = (refreshCalendarCells: () => void): void => {
  refreshCalendarCells()
  reloadInstrumentAndScheduleAfterVoid()
}

/** Keep Failures modal open with remaining ids after a void; empty means close. */
export const remainingFailureLogIdsAfterDelete = (
  instrumentTaskLogIds: number[],
  deletedLogId: number,
): number[] => instrumentTaskLogIds.filter((logId) => logId !== deletedLogId)

export interface FailureOccurrence {
  id: number
  completedAtLocal: string | null
}

export interface FailureRowModalState {
  date: string
  instrumentTaskLogIds: number[]
  // REBUILD-554: per-occurrence identity (completion time) aligned with instrumentTaskLogIds,
  // so the Failures dialog can label each failed occurrence and open the exact one.
  failures: FailureOccurrence[]
}

/**
 * Calendar Failures `onAfterDelete` state transition: drop the voided id; close when none remain.
 * Apply only after calendar `runFetch` applies non-null data (panel wires this via `runFetch`
 * onComplete) so modal ids cannot race ahead of Failures cell refresh (REBUILD-374 AC4).
 * Panel still starts `runCalendarLogDeleteRefresh` (cells + instrument + currentSchedule).
 */
export const failureRowModalStateAfterDelete = (
  previous: FailureRowModalState | null,
  deletedLogId: number,
): FailureRowModalState | null => {
  if (previous == null) return null
  const remainingIds = remainingFailureLogIdsAfterDelete(
    previous.instrumentTaskLogIds,
    deletedLogId,
  )
  if (remainingIds.length === 0) return null
  const remainingFailures = (previous.failures ?? []).filter(
    (failure) => failure.id !== deletedLogId,
  )
  return { date: previous.date, instrumentTaskLogIds: remainingIds, failures: remainingFailures }
}
